import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import { friendlyImpactTitle } from '@upright/common/utils';
import InfoTooltip from '@upright/ui/common/generic/components/icons/hero-icons/hi-question-mark-circle.svg';
import { TippyWithOriginalStyles } from '@upright/ui/common/generic/components/tooltips/default-tippy';
import { valenceColor } from '@upright/ui/common/generic/utilities/format';
import { zeroWidthNonBreakingSpace } from '@upright/ui/common/generic/utilities/zero-width-space';
import { SetStateAction, Dispatch } from 'react';
import styled from 'styled-components';
import { InfoBoxBottomPart } from '../../../../impact-lens-profile/common/components/info-boxes/info-box-bottom-part';
import { InfoBoxLinkButton } from '../../../../impact-lens-profile/common/components/info-boxes/info-box-link-button';
import { InfoBoxStepper } from '../../../../impact-lens-profile/common/components/info-boxes/info-box-stepper';
import { InfoBoxTitleRow } from '../../../../impact-lens-profile/common/components/info-boxes/info-box-title-row';
import { InsightOrTargetQuote } from '../../../../impact-lens-profile/common/components/info-boxes/styles';
import { ThreePartInfoBox } from '../../../../impact-lens-profile/common/components/info-boxes/three-part-info-box';
import { ImpactTargetOrSbtiTarget, SbtiTarget } from '../../common/types';
import { SingleSbtiTarget, SingleSbtiType } from './near-term-target';

interface Props {
  entityName?: string;
  target: SbtiTarget;
  id: string;
  targets: ImpactTargetOrSbtiTarget[];
  x: number;
  y: number;
  width: number;
  height: number;
  targetIndex: number;
  setTargetIndex: Dispatch<SetStateAction<number>>;
}

export const ScienceBasedTargetInfoBox = (props: Props) => {
  return (
    <ThreePartInfoBox
      width={props.width}
      height={props.height}
      x={props.x}
      y={props.y}
      id={props.id}
      contentTop={
        <>
          <ColorBlock style={{ backgroundColor: valenceColor(Valence.NEGATIVE) }}>
            {zeroWidthNonBreakingSpace}
          </ColorBlock>
          <InfoBoxTitleRow>
            <p>Targets on {friendlyImpactTitle(ImpactCategoryId.GHG_EMISSIONS)}</p>
          </InfoBoxTitleRow>
        </>
      }
      contentMiddle={
        <>
          <SbtiCardContent>
            <div>{props.entityName} has committed to Science-based targets.</div>
            <BoldText>Target summary</BoldText>
            <SingleSbtiTarget
              targetStatus={props.target.nearTermTargetStatus}
              targetClassification={props.target.nearTermTargetClassification}
              targetYear={props.target.nearTermTargetYear}
              type={SingleSbtiType.NEAR_TERM}
            />
            <SingleSbtiTarget
              targetStatus={props.target.longTermTargetStatus}
              targetClassification={props.target.longTermTargetClassification}
              targetYear={props.target.longTermTargetYear}
              type={SingleSbtiType.LONG_TERM}
            />
            <SingleSbtiTarget
              targetStatus={props.target.netZeroCommitted}
              targetYear={props.target.netZeroYear}
              type={SingleSbtiType.NET_ZERO}
            />
            {props.target.target && (
              <>
                <BoldText>Description</BoldText>
                <p>{props.target.target}</p>
              </>
            )}
          </SbtiCardContent>
        </>
      }
      contentBottom={
        <>
          <InfoBoxBottomPart
            left={
              <Link>
                <InfoBoxLinkButton
                  sourceLinkName="SBTi"
                  href="https://sciencebasedtargets.org/companies-taking-action#data-grid"
                />
                <TippyWithOriginalStyles
                  interactive={true}
                  trigger={'mouseenter focus'}
                  placement={'right'}
                  content={`Science-based targets (SBTi) provide a pathway for companies and financial institutions to reduce greenhouse gas (GHG) emissions. Read more at the SBTi website.`}
                >
                  <span>
                    <InfoTooltip />
                  </span>
                </TippyWithOriginalStyles>
              </Link>
            }
            right={
              <InfoBoxStepper total={props.targets.length} setIndex={props.setTargetIndex} index={props.targetIndex} />
            }
          />
        </>
      }
    />
  );
};

const ColorBlock = styled.div`
  height: 2px;
`;

const Link = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BoldText = styled.p`
  font-weight: 600;
`;

const SbtiCardContent = styled(InsightOrTargetQuote)`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;
